import { Link } from 'react-router-dom';
import { Coins, Loader2 } from 'lucide-react';

export type CreditPackRow = {
  id: string;
  code: string;
  name: string;
  credits: number;
  amount_paise: number;
};

type Props = {
  packs: CreditPackRow[];
  busyPackId: string | null;
  error: string | null;
  onBuy: (packId: string) => void;
  isGuest?: boolean;
};

function formatInrPaise(paise: number): string {
  const n = paise / 100;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

export function CreditPackGrid({ packs, busyPackId, error, onBuy, isGuest = false }: Props) {
  if (packs.length === 0) return null;

  return (
    <div className="w-full space-y-4">
      <p className="text-xs text-th-tertiary">
        One-time top-ups: credits are added to your current balance and do not expire. No subscription required.
      </p>
      {error ? (
        <p className="rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">{error}</p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {packs.map((pk) => (
          <div
            key={pk.id}
            className="flex flex-col rounded-xl border border-subtle bg-elevated/80 p-4 text-left"
          >
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-status-warning/15 text-status-warning">
                <Coins className="h-4 w-4" aria-hidden />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-th-primary">{pk.name}</h3>
                <p className="text-xs text-th-tertiary">{pk.credits.toLocaleString()} credits</p>
              </div>
            </div>
            <p className="mb-3 text-lg font-bold tabular-nums text-th-primary">{formatInrPaise(pk.amount_paise)}</p>
            {isGuest ? (
              <Link
                to="/login"
                className="mt-auto inline-flex items-center justify-center rounded-lg border border-subtle bg-surface px-3 py-2 text-sm font-medium text-th-primary hover:bg-overlay"
              >
                Sign in to buy
              </Link>
            ) : (
              <button
                type="button"
                disabled={!!busyPackId}
                onClick={() => onBuy(pk.id)}
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-lg border border-brand/50 bg-brand/10 px-3 py-2 text-sm font-medium text-brand hover:bg-brand/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busyPackId === pk.id ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Opening…
                  </>
                ) : (
                  'Buy credits'
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
